import { Stack, Typography } from '@mui/material';
import { ComponentProps } from 'react';
import * as React from 'react';

import { PopperContent } from '@/components/poppers/PopperContent';
import { ContextualHelpTooltip } from '@/modules/content/components/ContextualHelpTooltip';
import { COLORS } from '@/styles/tokens/colors';

export interface MiniTableRow {
  label: React.ReactNode;
  value: React.ReactNode | string;
  labelTooltip?: string;
  labelProps?: ComponentProps<typeof Typography>;
  valueProps?: ComponentProps<typeof Typography>;
  /**
   * When set, the row renders as an anchor that opens in a new tab, with a
   * hover affordance. Clicks on the row do not propagate to ancestors.
   */
  href?: string;
  /**
   * When set (and href is not), the row is clickable with the same hover
   * affordance as href. Clicks on the row do not propagate to ancestors.
   */
  onClick?: () => void;
}

interface Props {
  rows: MiniTableRow[];
  variant: 'default' | 'tally';
  rowProps?: ComponentProps<typeof Stack>;
}

export function MiniTable({ rows, variant, rowProps }: Props) {
  const tallyRow = variant === 'tally' ? rows.at(-1) : undefined;
  const displayRows = variant === 'tally' ? rows.slice(0, -1) : rows;

  return (
    <Stack spacing={1}>
      {displayRows.map((row, i) => {
        const Value =
          typeof row.value === 'string' ? (
            <Typography
              variant="title-xs"
              component="span"
              {...(row.valueProps ?? {})}
            >
              {row.value}
            </Typography>
          ) : (
            row.value
          );

        const rowContent = (
          <>
            <Stack direction="row" alignItems="center">
              <Typography
                component="span"
                variant="body-xs"
                {...(row.labelProps ?? {})}
                className="mini-table-row-label"
              >
                {row.label}
              </Typography>
              {row.labelTooltip && (
                <ContextualHelpTooltip>
                  <PopperContent body={row.labelTooltip} />
                </ContextualHelpTooltip>
              )}
            </Stack>
            {Value}
          </>
        );

        if (row.href) {
          return (
            <Stack
              component="a"
              href={row.href}
              target="_blank"
              rel="noopener noreferrer"
              // Rows can sit inside clickable containers (e.g. summary tiles);
              // following the link should not also trigger those.
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              key={`mini-table-row-${i}`}
              sx={{
                cursor: 'pointer',
                textDecoration: 'none',
                color: 'inherit',
                '&:hover .mini-table-row-label': {
                  textDecoration: 'underline',
                },
                ...(rowProps?.sx ?? {}),
              }}
            >
              {rowContent}
            </Stack>
          );
        }

        if (row.onClick) {
          const handleClick = row.onClick;
          return (
            <Stack
              role="button"
              tabIndex={0}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleClick();
              }}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClick();
                }
              }}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              key={`mini-table-row-${i}`}
              sx={{
                cursor: 'pointer',
                '&:hover .mini-table-row-label': {
                  textDecoration: 'underline',
                },
                ...(rowProps?.sx ?? {}),
              }}
            >
              {rowContent}
            </Stack>
          );
        }

        return (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            key={`mini-table-row-${i}`}
            sx={{
              px: variant === 'tally' ? 1 : 0,
              ...(rowProps?.sx ?? {}),
            }}
            {...rowProps}
          >
            {rowContent}
          </Stack>
        );
      })}
      {tallyRow && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ borderTop: `1px solid ${COLORS.NAVY[900]}`, px: 1, pt: 1 }}
        >
          <Stack direction="row" alignItems="center">
            <Typography variant="body-xs">{tallyRow.label}</Typography>
            {tallyRow.labelTooltip && (
              <ContextualHelpTooltip>
                <PopperContent body={tallyRow.labelTooltip} />
              </ContextualHelpTooltip>
            )}
          </Stack>
          <Typography
            variant="title-xs"
            component="span"
            {...(tallyRow.valueProps ?? {})}
          >
            {tallyRow.value}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
